import React, {useEffect} from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import "moment/locale/ro";

import {ITest} from "features/tests/test/components/_interfaces";
import {useAuth} from "hooks/useAuth";
import {niceUserName} from "utils/functions";
import Icon from "shared/ui/Icon";
import {MathJax} from "better-react-mathjax";

export interface MathPrompt {
    latex: string;
}

const MATHJAX_OWN_TEST_ITEM_TYPES = [2, 4, 5];
const MATHJAX_OTHER_MODES_ITEM_TYPES = [1 , 30, 34];
const MATHJAX_STANDARDS_ITEM_TYPES = [3, 4, 8, 9];
const MATHJAX_GRADE_IDS = [901, 902, 48];
const STANDARD_GRADES_NAMES = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];

export const MATH_PROMPT_E_FX_SQUARE: MathPrompt = {
    latex: "Se consideră $F$ o primitivă a funcției $f: \\mathbb{R} \\rightarrow \\mathbb{R}, f(x)=e^{x^2}$ si $l=\\lim _{x \\rightarrow \\infty} \\dfrac{x F(x)}{f(x)}$",
} as const;

const TestStartModal = (
    {
        test, startTest = () => {
    }, testFromClient
    }:
    { test: ITest; startTest: () => void, testFromClient: boolean }) => {
    const navigate = useNavigate();
    const {user, update, logout} = useAuth();
    const testStartsAt = test?.test_starts_at ? moment(test.test_starts_at) : moment(new Date(Date.now()));
    const currentDate = moment(new Date(Date.now()));



    const shouldConfirmMathJax = (test: ITest): boolean => {
        const hasTestItemTypeId = test.test_item_type_id != null;
        const hasTestGradeId = test.test_grade_id != null;

        const isOwnTestWithMathJaxItemType =
            hasTestItemTypeId &&
            test.mode === 7 &&
            MATHJAX_OWN_TEST_ITEM_TYPES.includes(test.test_item_type_id as number);

        const isMathJaxStandardItemType =
            hasTestItemTypeId &&
            (test.mode === 1 || test.mode === 2) &&
            MATHJAX_STANDARDS_ITEM_TYPES.includes(test.test_item_type_id as number) &&
            STANDARD_GRADES_NAMES.includes(test.test_grade_name as string);

        const isOtherModeWithMathJaxItemType =
            hasTestItemTypeId &&
            test.mode !== 7 &&
            MATHJAX_OTHER_MODES_ITEM_TYPES.includes(test.test_item_type_id as number) &&
            !STANDARD_GRADES_NAMES.includes(test.test_grade_name as string);

        const isMathJaxGrade =
            hasTestGradeId &&
            MATHJAX_GRADE_IDS.includes(test.test_grade_id as number);

        return isOwnTestWithMathJaxItemType || isOtherModeWithMathJaxItemType || isMathJaxStandardItemType || isMathJaxGrade;
    };

    const confirmMathJaxMessage = () => {
        return (
            <>
                <Typography>
                    Testele BRIO de matematică/fizică/chimie folosesc MATHJAX (o librărie vizuală pentru afișarea
                    formulelor), motiv pentru care, dacă browserul tău nu este compatibil cu aceasta, formulele nu vor
                    putea fi vizualizate corect.
                </Typography>
                <Typography variant="h2" mb={2}>Vezi mai jos exemplele:</Typography>
                <Box sx={{padding: 1.5, border: "1px solid #ccc", borderRadius: 1, mt: 2, mb: 2}}>

                    <Typography variant="body1"><b>Formulă MATHJAX:</b></Typography>
                    <MathJax inline dynamic>
                        {MATH_PROMPT_E_FX_SQUARE.latex}
                    </MathJax>
                    <Typography variant="body1" mb={2} mt={2}><b>Formulă afișată corect:</b></Typography>
                    <img src="/images/formula.png" alt=""/>
                </Box>
                <Typography variant="h2">Vezi corect formulele?</Typography>
            </>
        )
    }

    const confirmStartMessage = () => {
        return (
            <>
                <Typography variant="h1" mb={2}>
                    {test.title} #{test.test_id}
                </Typography>
                {testStartsAt.isAfter(currentDate) ? (
                    <Typography variant="h2" mb={2} sx={{color: "warning.main"}}>Testul poate fi accesat
                        la <strong>{moment(test.test_starts_at).locale("ro").format("HH:mm, DD MMMM YYYY")}</strong></Typography>
                ) : (
                    <>
                        <Box display="flex" alignItems="center" mt={2} mb={1}>
                            <Box mr={2}>
                                <Icon
                                    icon="fas fa-camera-cctv"
                                    fixedWidth={true}
                                    sx={{fontSize: "1.5rem", color: "error.main"}}
                                />
                            </Box>
                            <Box sx={{color: "secondary.light"}}>
                                <Typography><b>Informare test monitorizat</b></Typography>
                                <Typography>Sesiunea de testare este monitorizată la nivelul browserului, vă rugăm să vă
                                    dezactivați notificările, programele, și aplicațiile care rulează în
                                    background.</Typography>
                                <Typography>Orice părăsire a ecranului în timpul testului va fi înregistrată și
                                    transmisă
                                    profesorului tău.</Typography>
                            </Box>
                        </Box>
                        {test.test_config === 2 && (
                            <Box display="flex" alignItems="center" mt={2} mb={1}>
                                <Box mr={2}>
                                    <Icon
                                        icon="fas fa-octagon-exclamation"
                                        fixedWidth={true}
                                        sx={{fontSize: "1.5rem", color: "error.main"}}
                                    />
                                </Box>
                                <Typography sx={{color: "secondary.light"}}>
                                    Profesorul tău a ales să răspunzi în ordinea afișării întrebărilor, adică fără
                                    posibilitatea de a te întoarce la întrebările anterioare sau să le vezi pe
                                    următoarele.
                                </Typography>
                            </Box>
                        )}
                        <Typography>
                            Apasă pe butonul de mai jos{" "}
                            <Typography component="strong">
                                doar când ești pregătit să începi testul
                            </Typography>
                            , deoarece timpul de rezolvare alocat va porni automat odată cu această acțiune.
                        </Typography>
                        <Typography>
                            Test valabil până
                            la <strong>{moment(test.expires_at).locale("ro").format("HH:mm, DD MMMM YYYY")}</strong>
                        </Typography>
                    </>
                )}
            </>
        );
    };

    const confirmStart = () => {
        update("confirm", {
            show: true,
            closable: false,
            title: `CONFIRMARE ELEV - ${niceUserName(user.firstname, user.lastname, user.email).toUpperCase()}`,
            message: confirmStartMessage(),
            returnFunction: (callback: Function) => {
                startTest();
                update("confirm", {show: false});
            },
            cancelFunction: () => {
                if (testFromClient) {
                    logout();
                } else {
                    navigate("/teste");
                }
            },
            buttonYes: (
                <Box display="flex" gap={0.75} alignItems="center">
                    <Icon icon="far stopwatch" style={{fontSize: "1.5rem", top: 2, position: "relative"}}/>
                    START TEST
                </Box>
            ),
            buttonNo: "Renunță",
            disabledButtonYes: testStartsAt.isAfter(currentDate),
        });
    }

    const confirmMathJax = () => {
        update("confirm", {
            show: true,
            closable: false,
            title: `CONFIRMARE COMPATIBILITATE BROWSER`,
            message: confirmMathJaxMessage(),
            returnFunction: (callback: Function) => {
                update("confirm", {show: false});
                setTimeout(() => {
                    confirmStart();
                }, 100);
            },
            cancelFunction: () => {
                if (testFromClient) {
                    logout();
                } else {
                    navigate("/teste");
                }
            },
            buttonYes: (
                <Box display="flex" gap={0.75} alignItems="center">
                    DA, văd corect formulele
                </Box>
            ),
            buttonNo: "NU",
        });
    }

    useEffect(() => {
        // TEST WAS NOT STARTED
        if (shouldConfirmMathJax(test)) {
            confirmMathJax();
        } else {
            confirmStart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

export default TestStartModal;
